import React, { useState } from 'react';
import { BadgeDollarSignIcon, BriefcaseIcon, CheckIcon, CoinsIcon, GemIcon, GiftIcon, PercentIcon, TrendingUpIcon, UserCheckIcon } from 'lucide-react';
import { toast } from 'sonner';
import type { Employee, HistoryKey, HistoryRow, ProfileDetails } from '../../types/hr';
import type { IconComponent } from '../../types/ui';
import { directReports, displayName } from '../../utils/employees';
import { formatDate } from '../../utils/format';
import { hrActions } from '../../utils/hrStore';
import { Card } from '../ui/Card';
import { Field } from '../ui/Field';
import { AddEntryModal } from './AddEntryModal';
import { HistoryCard } from './HistoryCard';
import type { HistoryColumn } from './HistoryCard';
interface JobTabProps {
  employee: Employee;
  details: ProfileDetails;
}
interface TableConfig {
  key: HistoryKey;
  title: string;
  icon: IconComponent;
  columns: HistoryColumn[];
  markCurrent?: boolean;
}
const VETERAN_OPTIONS = ['Active Duty Wartime or Campaign Badge Veteran', 'Armed Forces Service Medal Veteran', 'Disabled Veteran', 'Recently Separated Veteran'];
const TABLES: Record<HistoryKey, TableConfig> = {
  employmentStatus: {
    key: 'employmentStatus',
    title: 'Employment Status',
    icon: UserCheckIcon,
    markCurrent: true,
    columns: [{
      key: 'date',
      label: 'Effective Date'
    }, {
      key: 'status',
      label: 'Status'
    }, {
      key: 'comment',
      label: 'Comment'
    }]
  },
  compensation: {
    key: 'compensation',
    title: 'Compensation',
    icon: BadgeDollarSignIcon,
    markCurrent: true,
    columns: [{
      key: 'date',
      label: 'Effective Date'
    }, {
      key: 'schedule',
      label: 'Pay Schedule'
    }, {
      key: 'payType',
      label: 'Pay Type'
    }, {
      key: 'rate',
      label: 'Pay Rate',
      align: 'right'
    }, {
      key: 'overtime',
      label: 'Overtime'
    }, {
      key: 'otRate',
      label: 'Overtime Rate'
    }, {
      key: 'reason',
      label: 'Change Reason'
    }, {
      key: 'comment',
      label: 'Comment'
    }]
  },
  jobInfo: {
    key: 'jobInfo',
    title: 'Job Information',
    icon: BriefcaseIcon,
    markCurrent: true,
    columns: [{
      key: 'date',
      label: 'Effective Date'
    }, {
      key: 'location',
      label: 'Location'
    }, {
      key: 'division',
      label: 'Division'
    }, {
      key: 'department',
      label: 'Department'
    }, {
      key: 'teams',
      label: 'Teams'
    }, {
      key: 'title',
      label: 'Job Title'
    }, {
      key: 'reportsTo',
      label: 'Reports To'
    }]
  },
  commission: {
    key: 'commission',
    title: 'Commission',
    icon: CoinsIcon,
    columns: [{
      key: 'date',
      label: 'Date'
    }, {
      key: 'amount',
      label: 'Amount',
      align: 'right'
    }, {
      key: 'comment',
      label: 'Comment'
    }]
  },
  potentialBonus: {
    key: 'potentialBonus',
    title: 'Potential Bonus',
    icon: PercentIcon,
    columns: [{
      key: 'percent',
      label: 'Annual %'
    }, {
      key: 'amount',
      label: 'Annual Amount (USD)',
      align: 'right'
    }]
  },
  bonus: {
    key: 'bonus',
    title: 'Bonus',
    icon: GiftIcon,
    columns: [{
      key: 'date',
      label: 'Date'
    }, {
      key: 'amount',
      label: 'Amount',
      align: 'right'
    }, {
      key: 'reason',
      label: 'Reason'
    }, {
      key: 'comment',
      label: 'Comment'
    }]
  },
  equity: {
    key: 'equity',
    title: 'Equity',
    icon: GemIcon,
    columns: [{
      key: 'grantType',
      label: 'Grant Type'
    }, {
      key: 'customName',
      label: 'Custom Grant Type Name'
    }, {
      key: 'grantDate',
      label: 'Grant Date'
    }, {
      key: 'vestingStart',
      label: 'Vesting Start'
    }, {
      key: 'granted',
      label: '# Granted',
      align: 'right'
    }, {
      key: 'strike',
      label: 'Strike Price',
      align: 'right'
    }, {
      key: 'schedule',
      label: 'Vesting Schedule'
    }, {
      key: 'months',
      label: 'Vesting Months',
      align: 'right'
    }, {
      key: 'cliff',
      label: 'Cliff Months',
      align: 'right'
    }]
  }
};
export function JobTab({
  employee,
  details
}: JobTabProps) {
  const [adding, setAdding] = useState<HistoryKey | null>(null);
  const reports = directReports(employee.id);
  const active = adding ? TABLES[adding] : null;
  const toggleVeteran = (option: string) => {
    const veteran = details.veteran.includes(option) ? details.veteran.filter(v => v !== option) : [...details.veteran, option];
    hrActions.saveDetails(employee.id, {
      ...details,
      veteran
    });
  };
  const saveRow = (row: HistoryRow) => {
    if (!active) return;
    hrActions.saveDetails(employee.id, {
      ...details,
      [active.key]: [row, ...details[active.key]]
    });
    toast.success(`${active.title} entry added`);
    setAdding(null);
  };
  const table = (key: HistoryKey) => {
    const cfg = TABLES[key];
    return <HistoryCard title={cfg.title} icon={cfg.icon} markCurrent={cfg.markCurrent} columns={cfg.columns} rows={details[key]} emptyText={`No ${cfg.title.toLowerCase()} entries have been added.`} onAdd={() => setAdding(key)} data-id="element-242" data-mp="2xfj0vy7svln9" />;
  };
  return <div className="space-y-6" data-id="element-243" data-mp="7uo04mdp2l6h">
      <Card title="Job Details" icon={BriefcaseIcon} data-id="element-244" data-mp="g8snhw892y24">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2 xl:grid-cols-3" data-id="element-245" data-mp="gbeyy6y9hxpm">
          <Field label="Hire Date" value={formatDate(employee.hireDate)} data-id="element-246" data-mp="kc8mv0bpfcii" />
          <Field label="EEO Job Category" value={details.eeoCategory} data-id="element-247" data-mp="6cfyhbnnh8dp" />
          <Field label="Company" value={details.company} data-id="element-248" data-mp="b9piucw6mtuo" />
          <Field label="Compensation Model" value={details.compensationModel} data-id="element-249" data-mp="bcbr6eqwdddy" />
          <Field label="NMLS #" value={<span className="font-mono text-[13px]" data-id="element-251" data-mp="z262uszhcvwm">{details.nmls}</span>} data-id="element-250" data-mp="z0bqw466se4o" />
          <Field label="Direct Reports" value={reports.length ? reports.map(displayName).join(', ') : 'None'} data-id="element-252" data-mp="13yd78hc89zcg" />
        </dl>

        <fieldset className="mt-6 border-t border-slate-100 pt-5" data-id="element-253" data-mp="p8602xro82r2">
          <legend className="text-[11px] font-medium uppercase tracking-wider text-muted" data-id="element-254" data-mp="u7ip9p60llwt">Veteran Status</legend>
          <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2" data-id="element-255" data-mp="1hxcw9q3nkg95">
            {VETERAN_OPTIONS.map(option => {
            const checked = details.veteran.includes(option);
            return <label key={option} className={`flex cursor-pointer items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm ring-1 ring-inset transition ${checked ? 'bg-azure/[0.06] text-navy ring-azure/30' : 'text-slate-600 ring-slate-200 hover:bg-canvas'}`} data-id="element-256" data-mp="1wja956mac61e">
                  <input type="checkbox" className="sr-only" checked={checked} onChange={() => toggleVeteran(option)} data-id="element-257" data-mp="1dnk8pnu03hpm" />
                  <span className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md ring-1 ring-inset ${checked ? 'bg-azure ring-azure' : 'bg-white ring-slate-300'}`} aria-hidden="true" data-id="element-258" data-mp="18u7m1j8vtkzi">
                    {checked && <CheckIcon className="h-3 w-3 text-white" strokeWidth={3} data-id="element-259" data-mp="uuzk4ilc3yrm" />}
                  </span>
                  {option}
                </label>;
          })}
          </div>
        </fieldset>
      </Card>

      {table('employmentStatus')}
      {table('compensation')}
      {table('jobInfo')}
      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2" data-id="element-260" data-mp="36htb82d3sebm">
        {table('commission')}
        {table('potentialBonus')}
      </div>
      {table('bonus')}
      {table('equity')}

      <p className="flex items-center justify-center gap-2 pb-2 text-xs text-mutedlight" data-id="element-261" data-mp="3firhkrb0kxxd">
        <TrendingUpIcon className="h-3.5 w-3.5" data-id="element-262" data-mp="2iuthn902a4j7" /> History is effective-dated — the top row is current.
      </p>

      <AddEntryModal open={Boolean(active)} title={active?.title ?? ''} columns={active?.columns ?? []} onClose={() => setAdding(null)} onSave={saveRow} data-id="element-263" data-mp="2mw6n3hz8s5pl" />
    </div>;
}